import Helpers from "@/helpers/helpers";
import {Value} from "patient-ping-remedy/packages/dropdown-list/lib/DropdownList";
import FileHelpers from "@/helpers/file_helpers";
import DisplayHelpers from "@/helpers/display_helpers";
import {
  AcknowledgementTrigger,
  AppSetting,
  AppSettingDataType,
  CareNoteTrigger,
  OutreachSummaryTrigger,
  User
} from "@/api/dto/dto";
import {RosterCodeBinding} from "@/components/pages/settings/form_templates/RosterCodeBinding";
import {InsurancePlanRow} from "@manual_encounters/encounter_update_status/pings/components/InsurancePlansFormNew";

export interface ValidationResponse {
  valid: boolean;
  message: string;
}

type RosterValueUser = {
  roster: Value;
  dataRole: Value;
  featureRole: Value;
}

type RosterValueSuperAdmin = {
  roster: Value;
}

const Validate = {
  // truthy required param in createFormFieldConfig means undefined/null/empty string/
  // whitespace-only strings are not allowed
  required(val: string, name: string, required: boolean): ValidationResponse {
    if (required && (Helpers.isEmpty(val) || !Helpers.isDefined(val))) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }
    const reBlank = /^\s+$/;
    const valid = !reBlank.test(val);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }
  },
  boolean(val: boolean | null): ValidationResponse {
    return {valid: !!val, message: ''};
  },
  firstName(val: string, name: string, required: boolean, user?: User): ValidationResponse {
    // if user is SSO, first name is not required
    if (user && user.sso_connection) {
      return {valid: true, message: ''};
    }

    if (required && (Helpers.isEmpty(val) || !Helpers.isDefined(val))) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    const re = /^[a-zA-Z \-']{2,25}$/;
    const valid = !val || re.test(val);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {
        valid: false,
        message: 'First name must be at least 2-25 characters and include only alphabetical characters or - \''
      };
    }
  },
  lastName(val: string, name: string, required: boolean, user?: User): ValidationResponse {
    // if user is SSO, last name is not required
    if (user && user.sso_connection) {
      return {valid: true, message: ''};
    }

    if (required && (Helpers.isEmpty(val) || !Helpers.isDefined(val))) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    const re = /^[a-zA-Z \-',.]{2,50}$/;
    const valid = !val || re.test(val);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {
        valid: false,
        message: 'Last name must be at least 2-50 characters and include only alphabetical characters or - \' . ,'
      };
    }
  },

  formName(val: string, name: string, required: boolean): ValidationResponse {
    if (required && (Helpers.isEmpty(val) || !Helpers.isDefined(val))) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    const re = /^[A-Za-z0-9_'\-\s.()]{2,200}$/;
    const valid = re.test(val);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {
        valid: false, message: 'The file name must be between 2 and 200 characters long, ' +
          'containing only letters (A-Z, a-z), numbers (0-9), underscores (_), period (.), parentheses (()), ' +
          'and/or hyphens (-).'
      };
    }
  },
  formScore(val: string, name: string, required: boolean): ValidationResponse {
    if (required && (Helpers.isEmpty(val) || !Helpers.isDefined(val))) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    const re = /^[A-Za-z0-9_ '\-\s.]{0,50}$/;
    const valid = re.test(val);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {
        valid: false, message: 'The score can be up to 50 characters long and can only ' +
          'include letters (A-Z, a-z), numbers (0-9), periods (.), spaces ( ), and/or hyphens (-).'
      };
    }
  },
  validateRostersUsers(val: RosterValueUser[], name: string, required: boolean) {
    if (!Helpers.isDefined(val) && required) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    if (val?.length === 0) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} requires at least one selection.`};
    }

    const valid = val?.every((v: RosterValueUser) => {
      return !Helpers.isEmpty(v.roster?.value) && !Helpers.isEmpty(v.dataRole?.value) && !Helpers.isEmpty(v.featureRole?.value);
    });

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} requires a valid selection for each roster.`};
    }
  },
  validateRostersSuperAdmin(val: RosterValueSuperAdmin[], name: string, required: boolean) {
    if (!Helpers.isDefined(val) && required) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    if (val?.length === 0) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} requires at least one selection.`};
    }

    const valid = val?.every((v: RosterValueSuperAdmin) => {
      return !Helpers.isEmpty(v.roster?.value);
    });

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} requires a valid selection for each roster.`};
    }
  },
  email(email: string, name: string, required: boolean): ValidationResponse {
    if (required && Helpers.isEmpty(email)) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    const re = /\S+@\S+\.\S+/;
    const valid = re.test(email);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: 'Please provide a valid email address.'};
    }
  },
  phone(phone: string, name: string, SSO: boolean): ValidationResponse {
    if (SSO && Helpers.isEmpty(phone)) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    if (!SSO && (Helpers.isEmpty(phone) || !Helpers.isDefined(phone))) {
      return {valid: true, message: ``};
    }

    const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
    const valid = re.test(phone);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: 'Phone number must be in the format (XXX) XXX-XXXX.'};
    }
  },
  validateIntegerBetween(min: number, max: number) {
    return (val: string | null | undefined, name: string, required: boolean): ValidationResponse => {
      const valid = this.validateInteger(val, name, required);

      if (!valid.valid || Number(val) < min || Number(val) > max) {
        return {valid: false, message: `${DisplayHelpers.humanize(name)} must be a number between ${min}-${max}.`};
      }

      return {valid: true, message: ''};
    };
  },
  validateInteger(val: string | null | undefined, name: string, required: boolean): ValidationResponse {
    if (!Helpers.isDefined(val) && required) {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }

    const valid = !Number.isNaN(Number(val));

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} must be a number.`};
    }
  },
  validateLength(length: number) {
    return (val: string, name: string, required: boolean): ValidationResponse => {
      if (required && !Helpers.isDefined(val)) {
        return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
      }

      if (val && val.length < length) {
        return {valid: false, message: `${DisplayHelpers.humanize(name)} must be at least ${length} characters.`};
      }

      return {valid: true, message: ''};
    };
  },
  validateFile(size?: number, types?: string[]) {
    return (val: FileList | File[] | File, name: string, required: boolean): ValidationResponse => {
      if (required && (!Helpers.isDefined(val) || (!(val instanceof File) && val.length === 0))) {
        return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
      }

      if (!required && !Helpers.isDefined(val)) {
        return {valid: true, message: ''};
      }

      const files = 'length' in val ? Array.from(val) : [val];

      const validSize = files.every((file: File) => {
        return file.size < (size ?? Infinity);
      });
      const validType = files.every((file: File) => {
        return (types?.includes(file.type) ?? true);
      });

      if (!validSize) {
        return {valid: false, message: `Please provide a file under ${FileHelpers.formatBits(size ?? 0)} in size.`};
      } else if (!validType) {
        return {valid: false, message: `Please provide a file with valid type.`};
      } else {
        return {valid: true, message: ''};
      }
    };
  },
  validateSsn(val: string, name: string, required: boolean) {
    if (!required && (Helpers.isEmpty(val) || !Helpers.isDefined(val))) {
      return {valid: true, message: ''};
    }

    const re = /^(?!0000)[0-9]{4}$/;
    const valid = re.test(val);

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }
  },
  validateSln(val: any): ValidationResponse {
    // not required, do not need checks
    if (!Helpers.isEmpty(val.value)) {
      if (Helpers.isEmpty(val.type) || Helpers.isEmpty(val.jurisdiction) || !Helpers.isDefined(val.jurisdiction)) {
        return {valid: false, message: ''};
      }
    }

    if (!Helpers.isEmpty(val.type)) {
      if (Helpers.isEmpty(val.value) || Helpers.isEmpty(val.jurisdiction) || !Helpers.isDefined(val.jurisdiction)) {
        return {valid: false, message: ''};
      }
    }

    if (!Helpers.isEmpty(val.jurisdiction) && Helpers.isDefined(val.jurisdiction)) {
      if (Helpers.isEmpty(val.value) || Helpers.isEmpty(val.type)) {
        return {valid: false, message: ''};
      }
    }

    return {valid: true, message: ''};
  },
  validateAppSettings(val: AppSetting, name: string, required: boolean): ValidationResponse {
    if (val.dataType === AppSettingDataType.INTEGER) {
      const response = Validate.required(val.value, name, required);
      if (response.valid) {
        return Validate.validateInteger(val.value, name, required);
      }
      return response;
    }
    return Validate.required(val.value, name, required);
  },
  validateFormTemplateRosterBindings(val: RosterCodeBinding[]) {
    const valid = val.every(binding =>
      Helpers.isDefined(binding.codeBinding) && !Helpers.isEmpty(binding.codeBinding.value)
        && Helpers.isDefined(binding.rosters) && binding.rosters.length > 0
    );

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: ''};
    }
  },
  validateCareNoteTriggers(val: CareNoteTrigger[]) {
    const valid = val.every(trigger =>
      !!trigger.taskType && !!trigger.step && !!trigger.stepStatus
    );

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: ''};
    }
  },
  validateAcknowledgementTriggers(val: AcknowledgementTrigger[]) {
    const hasEmptyFields = val.some(trigger =>
      !trigger.journeyType || !trigger.recipient || !trigger.channel || !trigger.secureEmailTemplateId);
    const pairs = val
      .filter(trigger => !!trigger.journeyType && !!trigger.recipient)
      .map(trigger => `${trigger.journeyType}::${trigger.recipient}`);
    const hasDuplicatePairs = new Set(pairs).size !== pairs.length;

    const valid = !hasDuplicatePairs && !hasEmptyFields;

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: ''};
    }
  },
  validateOutreachSummaryTriggers(val: OutreachSummaryTrigger[]) {
    const hasEmptyFields = val.some(trigger => !trigger.taskType || !trigger.delay);
    const hasNegativeDelay = val.some(trigger =>
      trigger.delay != null && trigger.delay !== '' && Number(trigger.delay) < 0);
    const taskTypes = val.map(trigger => trigger.taskType).filter(Boolean);
    const hasDuplicateTaskTypes = new Set(taskTypes).size !== taskTypes.length;

    const valid = !hasEmptyFields && !hasNegativeDelay && !hasDuplicateTaskTypes;

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: ''};
    }
  },
  validateAdmittedFrom(val: Value, name: string, required: boolean) {
    if (!required && (Helpers.isEmpty(val?.label) || !Helpers.isDefined(val?.label))) {
      return {valid: true, message: ''};
    }

    const trimmedValueLength = val?.label?.trim()?.length;
    const valid = trimmedValueLength > 0 && trimmedValueLength < 151;

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }
  },
  validateInsurancePlans(val: InsurancePlanRow[], name: string) {
    const valid = val.every((plan) => {
      const provider = plan.insuranceProvider ?? {};
      const insuranceCompanyName = provider.insuranceCompanyName;
      const isNoInsurance = insuranceCompanyName === 'No Insurance';
      const isSelfPay = insuranceCompanyName === 'Self Pay';

      const providerValid = !!provider.code || isNoInsurance;

      const insuranceValueRequired = !isNoInsurance && !isSelfPay;
      const insuranceValueValid = !insuranceValueRequired || !Helpers.isEmpty(plan.insuranceValue ?? '');

      const otherNameRequired = provider.code === 'OTHER';
      const otherNameValid = !otherNameRequired || !Helpers.isEmpty(plan.otherTranslatedName ?? '');

      return providerValid && insuranceValueValid && otherNameValid;
    });

    if (valid) {
      return {valid: true, message: ''};
    } else {
      return {valid: false, message: `${DisplayHelpers.humanize(name)} is required.`};
    }
  },
};

export default Validate;
